import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  format,
  startOfDay,
  endOfDay,
  addDays,
  subDays,
  isToday,
} from "date-fns";
import { Router } from "@vaadin/router";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "../components/app-shell";
import "../components/cgm-graph";
import { getDb } from "../db";
import { syncService } from "../services/sync";
import type { LogEntry, CgmReading } from "../types";
import {
  PERIOD_TAG_INFO,
  formatInsulinDose,
  getQATotal,
  getBITotal,
  calculateIOB,
} from "../types";
import {
  analyzePatterns,
  generatePredictions,
  getCurrentPeriod,
  type PatternResult,
  type PredictionResult,
} from "../utils/pattern-analysis";

@customElement("home-page")
export class HomePage extends LitElement {
  @state() private currentDate = new Date();
  @state() private entries: LogEntry[] = [];
  @state() private cgmReadings: CgmReading[] = [];
  @state() private hasCgmData = false;
  @state() private cgmDateRange: { start: Date; end: Date } | null = null;
  @state() private predictions: PredictionResult[] = [];
  @state() private historicalPatterns: PatternResult[] = [];

  static styles = css`
    :host {
      display: block;
    }

    .date-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem;
    }

    .date-nav {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .date-nav sl-button::part(base) {
      min-width: 36px;
      padding: 0.25rem;
    }

    .date-info h2 {
      margin: 0 0 0.25rem 0;
      font-size: 1.125rem;
      font-weight: 600;
    }

    .date-info p {
      margin: 0;
      color: #6c757d;
      font-size: 0.875rem;
    }

    .cgm-range-notice {
      background: #fff3cd;
      border: 1px solid #ffc107;
      border-radius: 0.5rem;
      padding: 0.75rem 1rem;
      margin: 0 1rem 1rem;
      font-size: 0.875rem;
    }

    .cgm-range-notice a {
      color: #856404;
      font-weight: 500;
    }

    .entries-container {
      padding: 0 1rem 6rem;
    }

    .entry-card {
      background: white;
      border-radius: 0.5rem;
      border: 1px solid #dee2e6;
      padding: 0.75rem 1rem;
      margin-bottom: 0.75rem;
    }

    .entry-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.5rem;
    }

    .entry-time {
      font-weight: 600;
      font-size: 1rem;
    }

    .entry-period {
      background: #e7f1ff;
      color: #0d6efd;
      padding: 0.125rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .entry-data {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .entry-field {
      display: flex;
      flex-direction: column;
      min-width: 50px;
    }

    .entry-field .label {
      font-size: 0.625rem;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .entry-field .value {
      font-size: 1rem;
      font-weight: 500;
    }

    /* GL color coding */
    .gl-hypo {
      color: #dc3545;
      font-weight: 700;
    }
    .gl-low {
      color: #fd7e14;
    }
    .gl-target {
      color: #198754;
    }
    .gl-elevated {
      color: #fd7e14;
    }
    .gl-high {
      color: #dc3545;
    }

    /* Ketone color coding */
    .ket-normal {
      color: #198754;
    }
    .ket-elevated {
      color: #fd7e14;
    }
    .ket-high {
      color: #dc3545;
    }
    .ket-danger {
      color: #fff;
      background: #dc3545;
      padding: 0 0.25rem;
      border-radius: 0.25rem;
    }

    /* Stacking indicator */
    .stacking-warning {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      color: #fd7e14;
      font-size: 0.625rem;
      margin-left: 0.25rem;
    }

    /* Entry card variants */
    .entry-card.has-hypo {
      border-left: 3px solid #dc3545;
    }
    .entry-card.has-high-ket {
      border-left: 3px solid #dc3545;
    }
    .entry-card.has-stacking {
      border-left: 3px solid #fd7e14;
    }

    .entry-note {
      margin-top: 0.5rem;
      font-size: 0.875rem;
      color: #495057;
      font-style: italic;
    }

    .empty-state {
      text-align: center;
      padding: 3rem 1rem;
      color: #6c757d;
    }

    .fab {
      position: fixed;
      bottom: 5rem;
      right: 1rem;
      z-index: 1000;
    }

    .fab sl-button::part(base) {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      font-size: 1.5rem;
    }

    @media (min-width: 1024px) {
      .fab {
        bottom: 2rem;
        right: 2rem;
      }

      .entries-container {
        max-width: 600px;
        padding-bottom: 2rem;
      }
    }

    .graph-section {
      padding: 0 1rem;
      margin-bottom: 1rem;
    }

    .graph-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .graph-header h3 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      color: #495057;
    }

    .graph-header a {
      font-size: 0.75rem;
      color: #0d6efd;
      text-decoration: none;
    }

    /* Summary Section */
    .summary-section {
      padding: 0 1rem;
      margin-bottom: 1rem;
    }

    .summary-cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 0.5rem;
    }

    .summary-card {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 0.75rem 0.5rem;
      text-align: center;
    }

    .summary-card.iob {
      background: #fff3cd;
    }

    .summary-card .value {
      font-size: 1.125rem;
      font-weight: 600;
      color: #212529;
    }

    .summary-card .label {
      font-size: 0.625rem;
      color: #6c757d;
      text-transform: uppercase;
      margin-top: 0.25rem;
    }

    .summary-card .value.good {
      color: #198754;
    }

    .summary-card .value.warning {
      color: #fd7e14;
    }

    .summary-card .value.neutral {
      color: #6c757d;
    }

    .graph-links {
      display: flex;
      gap: 0.75rem;
    }

    /* Predictions section */
    .predictions-section {
      padding: 0 1rem 1rem;
    }

    .predictions-section h3 {
      font-size: 0.75rem;
      font-weight: 600;
      color: #495057;
      text-transform: uppercase;
      margin: 0 0 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .prediction-cards {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .prediction-card {
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      padding: 0.75rem;
    }

    .prediction-card.high-risk {
      background: #fff5f5;
      border-color: #feb2b2;
    }

    .prediction-card.medium-risk {
      background: #fffaf0;
      border-color: #fbd38d;
    }

    .prediction-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .prediction-type {
      font-weight: 600;
      font-size: 0.85rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .prediction-likelihood {
      font-size: 0.65rem;
      padding: 0.15rem 0.4rem;
      border-radius: 0.25rem;
      font-weight: 600;
      text-transform: uppercase;
    }

    .prediction-likelihood.high {
      background: #dc3545;
      color: white;
    }

    .prediction-likelihood.medium {
      background: #fd7e14;
      color: white;
    }

    .prediction-likelihood.low {
      background: #6c757d;
      color: white;
    }

    .prediction-body {
      font-size: 0.8rem;
      color: #495057;
    }

    .prediction-based-on {
      margin-bottom: 0.25rem;
    }

    .prediction-timeframe {
      color: #6c757d;
      font-size: 0.75rem;
    }

    .prediction-action {
      margin-top: 0.5rem;
      padding: 0.5rem;
      background: #f8f9fa;
      border-radius: 0.375rem;
      border-left: 3px solid #0d6efd;
      font-size: 0.75rem;
    }

    .no-predictions {
      text-align: center;
      padding: 0.75rem;
      color: #6c757d;
      background: #f8f9fa;
      border-radius: 0.375rem;
      font-size: 0.8rem;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    // Sync first if online to get latest data from other devices
    if (navigator.onLine) {
      await syncService.checkAndSync();
    }
    await this._loadEntries();
    await this._loadCgmReadings();
    await this._loadPredictions();
  }

  private async _loadEntries() {
    const dayStart = startOfDay(this.currentDate);
    const dayEnd = endOfDay(this.currentDate);

    try {
      this.entries = await getDb()
        .entries.where("timestamp")
        .between(dayStart, dayEnd)
        .sortBy("timestamp");
    } catch (error) {
      console.error("Failed to load entries:", error);
    }
  }

  private async _loadCgmReadings() {
    try {
      const db = getDb();
      const count = await db.cgmReadings.count();
      this.hasCgmData = count > 0;

      if (this.hasCgmData) {
        const dayStart = startOfDay(this.currentDate).getTime();
        const dayEnd = endOfDay(this.currentDate).getTime();

        // Filter in JS because IndexedDB date comparisons can be unreliable
        const allReadings = await db.cgmReadings.toArray();

        // Helper to get timestamp as number
        const getTs = (t: Date | string | number | unknown): number => {
          if (t instanceof Date) return t.getTime();
          if (typeof t === "string") return new Date(t).getTime();
          if (typeof t === "number") return t;
          return new Date(t as string).getTime();
        };

        // Calculate date range of all CGM data
        if (allReadings.length > 0) {
          let minTs = Infinity;
          let maxTs = -Infinity;
          for (const r of allReadings) {
            const ts = getTs(r.timestamp);
            if (ts < minTs) minTs = ts;
            if (ts > maxTs) maxTs = ts;
          }
          this.cgmDateRange = {
            start: new Date(minTs),
            end: new Date(maxTs),
          };
        }

        this.cgmReadings = allReadings
          .filter((r) => {
            const ts = getTs(r.timestamp);
            return ts >= dayStart && ts <= dayEnd;
          })
          .sort((a, b) => getTs(a.timestamp) - getTs(b.timestamp));
      }
    } catch (error) {
      console.error("Failed to load CGM readings:", error);
    }
  }

  private async _loadPredictions() {
    if (!isToday(this.currentDate)) {
      // Only show predictions for today
      this.predictions = [];
      return;
    }

    try {
      // Get all historical data for pattern analysis
      const db = getDb();
      const [allReadings, allEntries] = await Promise.all([
        db.cgmReadings.toArray(),
        db.entries.toArray(),
      ]);

      // Analyze patterns from recent history
      this.historicalPatterns = analyzePatterns({
        cgmReadings: allReadings,
        entries: allEntries,
        daysToAnalyze: 7,
        targetLow: 4,
        targetHigh: 10,
      });

      // Generate predictions based on today's entries and patterns
      this.predictions = generatePredictions(
        this.entries,
        this.cgmReadings,
        this.historicalPatterns,
        getCurrentPeriod(),
      );
    } catch (error) {
      console.error("Failed to load predictions:", error);
    }
  }

  private async _changeDate(newDate: Date) {
    this.currentDate = newDate;
    await this._loadEntries();
    await this._loadCgmReadings();
    await this._loadPredictions();
  }

  private _prevDay() {
    this._changeDate(subDays(this.currentDate, 1));
  }

  private _nextDay() {
    this._changeDate(addDays(this.currentDate, 1));
  }

  private _goToToday() {
    this._changeDate(new Date());
  }

  private _goToLatestCgm() {
    if (this.cgmDateRange) {
      this._changeDate(this.cgmDateRange.end);
    }
  }

  private _openAddEntry() {
    Router.go("/add");
  }

  private _calculateActiveIOB(): number {
    const now = new Date();
    let totalIOB = 0;

    for (const entry of this.entries) {
      const qaTotal = getQATotal(entry);
      if (qaTotal) {
        const hoursElapsed =
          (now.getTime() - entry.timestamp.getTime()) / (1000 * 60 * 60);
        if (hoursElapsed >= 0 && hoursElapsed <= 5) {
          totalIOB += calculateIOB(qaTotal, hoursElapsed);
        }
      }
    }

    return Math.round(totalIOB * 10) / 10;
  }

  private _renderSummary() {
    // Calculate totals for the day
    let totalCarbs = 0;
    let totalQA = 0;
    let totalBI = 0;

    for (const entry of this.entries) {
      if (entry.carbs) totalCarbs += entry.carbs;
      const qa = getQATotal(entry);
      if (qa) totalQA += qa;
      const bi = getBITotal(entry);
      if (bi) totalBI += bi;
    }

    const activeIOB = this._calculateActiveIOB();

    return html`
      <div class="summary-section">
        <div class="summary-cards">
          <div class="summary-card iob">
            <div class="value ${activeIOB > 0 ? "warning" : "neutral"}">
              ${activeIOB}u
            </div>
            <div class="label">IOB</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalCarbs}g</div>
            <div class="label">Carbs</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalQA}u</div>
            <div class="label">Quick</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalBI > 0 ? `${totalBI}u` : "-"}</div>
            <div class="label">Long</div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderPredictions() {
    // Only show predictions for today
    if (!isToday(this.currentDate)) {
      return "";
    }

    const getPredictionIcon = (type: PredictionResult["type"]): string => {
      switch (type) {
        case "hypo-risk":
          return "⬇️";
        case "hyper-risk":
          return "⬆️";
        case "stacking":
          return "⚠️";
        case "bg-trajectory":
          return "📈";
        default:
          return "💡";
      }
    };

    const getPredictionLabel = (type: PredictionResult["type"]): string => {
      switch (type) {
        case "hypo-risk":
          return "Hypo Risk";
        case "hyper-risk":
          return "High BG Risk";
        case "stacking":
          return "Insulin Stacking";
        case "bg-trajectory":
          return "BG Trend";
        default:
          return "Alert";
      }
    };

    return html`
      <div class="predictions-section">
        <h3>🔮 Predictions</h3>
        ${this.predictions.length === 0
          ? html`<div class="no-predictions">
              No predictions at this time. Continue logging to enable
              predictions.
            </div>`
          : html`
              <div class="prediction-cards">
                ${this.predictions.map(
                  (pred) => html`
                    <div
                      class="prediction-card ${pred.likelihood === "high"
                        ? "high-risk"
                        : pred.likelihood === "medium"
                          ? "medium-risk"
                          : ""}"
                    >
                      <div class="prediction-header">
                        <span class="prediction-type">
                          ${getPredictionIcon(pred.type)}
                          ${getPredictionLabel(pred.type)}
                        </span>
                        <span class="prediction-likelihood ${pred.likelihood}">
                          ${pred.likelihood}
                        </span>
                      </div>
                      <div class="prediction-body">
                        <div class="prediction-based-on">${pred.basedOn}</div>
                        <div class="prediction-timeframe">
                          Timeframe: ${pred.timeframe}
                        </div>
                        ${pred.action
                          ? html`<div class="prediction-action">
                              ${pred.action}
                            </div>`
                          : ""}
                      </div>
                    </div>
                  `,
                )}
              </div>
            `}
      </div>
    `;
  }

  private _getGlClass(value?: number): string {
    if (!value) return "";
    if (value < 4) return "gl-hypo";
    if (value < 5) return "gl-low";
    if (value <= 7) return "gl-target";
    if (value <= 10) return "gl-elevated";
    return "gl-high";
  }

  private _getKetClass(value?: number): string {
    if (!value) return "";
    if (value < 0.6) return "ket-normal";
    if (value < 1.5) return "ket-elevated";
    if (value < 3.0) return "ket-high";
    return "ket-danger";
  }

  private _hasStackingConcern(entry: LogEntry): boolean {
    const qaTotal = getQATotal(entry);
    if (!qaTotal) return false;

    const entryTime = entry.timestamp.getTime();
    const threeHoursAgo = entryTime - 3 * 60 * 60 * 1000;

    for (const prior of this.entries) {
      if (prior.id === entry.id) continue;
      const priorTime = prior.timestamp.getTime();
      if (priorTime >= threeHoursAgo && priorTime < entryTime) {
        const priorQA = getQATotal(prior);
        if (priorQA) return true;
      }
    }
    return false;
  }

  private _getEntryCardClass(entry: LogEntry): string {
    const classes = ["entry-card"];
    if (entry.bloodGlucose && entry.bloodGlucose < 4) classes.push("has-hypo");
    else if (entry.ketones && entry.ketones >= 1.5)
      classes.push("has-high-ket");
    else if (this._hasStackingConcern(entry)) classes.push("has-stacking");
    return classes.join(" ");
  }

  private _renderEntry(entry: LogEntry) {
    const qaTotal = getQATotal(entry);
    const biTotal =
      entry.biBase !== undefined || entry.biCorrection !== undefined
        ? (entry.biBase || 0) + (entry.biCorrection || 0)
        : undefined;
    const hasData =
      entry.bloodGlucose || entry.carbs || qaTotal || biTotal || entry.ketones;
    const hasStacking = this._hasStackingConcern(entry);

    return html`
      <div class=${this._getEntryCardClass(entry)}>
        <div class="entry-header">
          <span class="entry-time">${format(entry.timestamp, "HH:mm")}</span>
          <span class="entry-period"
            >${PERIOD_TAG_INFO[entry.periodTag].label}</span
          >
        </div>
        ${hasData
          ? html`
              <div class="entry-data">
                ${entry.bloodGlucose
                  ? html`
                      <div class="entry-field">
                        <span class="label">GL</span>
                        <span
                          class="value ${this._getGlClass(entry.bloodGlucose)}"
                          >${entry.bloodGlucose.toFixed(1)}</span
                        >
                      </div>
                    `
                  : ""}
                ${entry.carbs
                  ? html`
                      <div class="entry-field">
                        <span class="label">CP</span>
                        <span class="value">${entry.carbs}g</span>
                      </div>
                    `
                  : ""}
                ${qaTotal !== undefined
                  ? html`
                      <div class="entry-field">
                        <span class="label">Quick</span>
                        <span class="value">
                          ${formatInsulinDose(
                            entry.qaBase,
                            entry.qaCorrection,
                          )}u
                          ${hasStacking
                            ? html`<span class="stacking-warning">⚠️ IOB</span>`
                            : ""}
                        </span>
                      </div>
                    `
                  : ""}
                ${biTotal !== undefined
                  ? html`
                      <div class="entry-field">
                        <span class="label">Long</span>
                        <span class="value"
                          >${formatInsulinDose(
                            entry.biBase,
                            entry.biCorrection,
                          )}u</span
                        >
                      </div>
                    `
                  : ""}
                ${entry.ketones !== undefined
                  ? html`
                      <div class="entry-field">
                        <span class="label">Ket</span>
                        <span class="value ${this._getKetClass(entry.ketones)}"
                          >${entry.ketones.toFixed(1)}</span
                        >
                      </div>
                    `
                  : ""}
              </div>
            `
          : ""}
        ${entry.notes
          ? html` <div class="entry-note">${entry.notes}</div> `
          : ""}
      </div>
    `;
  }

  render() {
    const dateRange = {
      start: startOfDay(this.currentDate),
      end: endOfDay(this.currentDate),
    };

    const todayDate = isToday(this.currentDate);
    const outsideCgmRange =
      this.cgmDateRange &&
      (this.currentDate < this.cgmDateRange.start ||
        this.currentDate > this.cgmDateRange.end);

    return html`
      <app-shell pageTitle="Today">
        <div class="date-header">
          <div class="date-info">
            <h2>
              ${todayDate ? "Today" : format(this.currentDate, "EEE d MMM")}
            </h2>
            <p>${format(this.currentDate, "EEEE, d MMMM yyyy")}</p>
          </div>
          <div class="date-nav">
            <sl-button size="small" variant="text" @click=${this._prevDay}>
              <sl-icon name="chevron-left"></sl-icon>
            </sl-button>
            <sl-button
              size="small"
              variant="text"
              @click=${this._goToToday}
              ?disabled=${todayDate}
            >
              Today
            </sl-button>
            <sl-button size="small" variant="text" @click=${this._nextDay}>
              <sl-icon name="chevron-right"></sl-icon>
            </sl-button>
          </div>
        </div>

        ${outsideCgmRange && this.cgmDateRange
          ? html`
              <div class="cgm-range-notice">
                CGM data available:
                ${format(this.cgmDateRange.start, "d MMM yyyy")} -
                ${format(this.cgmDateRange.end, "d MMM yyyy")}
                <a href="#" @click=${this._goToLatestCgm}>Jump to latest</a>
              </div>
            `
          : ""}
        ${this.hasCgmData
          ? html`
              <div class="graph-section">
                <div class="graph-header">
                  <h3>
                    Glucose Trend
                    ${this.cgmReadings.length > 0
                      ? `(${this.cgmReadings.length} readings)`
                      : ""}
                  </h3>
                  <div class="graph-links">
                    <a href="/history">History</a>
                    <a href="/upload">Import</a>
                  </div>
                </div>
                <cgm-graph
                  .readings=${this.cgmReadings}
                  .entries=${this.entries}
                  .dateRange=${dateRange}
                  height=${180}
                  ?hasAnyData=${this.hasCgmData}
                ></cgm-graph>
              </div>
            `
          : ""}
        ${this._renderSummary()} ${this._renderPredictions()}

        <div class="entries-container">
          ${this.entries.length === 0
            ? html`
                <div class="empty-state">
                  <p>
                    No entries yet today.<br />Tap + to log your first entry.
                  </p>
                </div>
              `
            : this.entries.map((e) => this._renderEntry(e))}
        </div>

        <div class="fab">
          <sl-button
            variant="primary"
            size="large"
            circle
            @click=${this._openAddEntry}
          >
            <sl-icon name="plus-lg"></sl-icon>
          </sl-button>
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "home-page": HomePage;
  }
}
